/**
 * Iter 220i — In-app notification bell for admin/office users.
 * Reads `/notifications/me` (recipient_type=user) and shows unread
 * count. Click opens a lightweight dropdown listing recent items.
 * Marking one read PATCHes /notifications/{id}/read.
 */
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, CheckCircle, X, ClipboardText } from "@phosphor-icons/react";
import { api } from "../../lib/api";

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  const loadCount = async () => {
    try {
      const r = await api.get("/notifications/me/unread-count");
      setUnread(r.data.unread || 0);
    } catch { /* ignore */ }
  };
  const loadList = async () => {
    try {
      const r = await api.get("/notifications/me?limit=15");
      setItems(r.data.notifications || []);
    } catch { /* ignore */ }
  };
  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, 60_000); // poll every 60 s
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) loadList();
  };

  const markRead = async (id) => {
    try {
      await api.patch(`/notifications/${id}/read`);
      setItems((prev) => prev.map((n) => n.id === id ? { ...n, read_at: new Date().toISOString() } : n));
      loadCount();
    } catch { /* ignore */ }
  };
  const markAll = async () => {
    try {
      await api.post("/notifications/mark-all-read");
      setItems((prev) => prev.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
      setUnread(0);
    } catch { /* ignore */ }
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={toggle} data-testid="user-notif-bell"
              className="relative p-2 rounded-md text-[#5C5A55] hover:text-[#2D5A27] hover:bg-[#F7F6F2] transition-colors"
              title="Notifications">
        <Bell size={18} weight={unread > 0 ? "fill" : "regular"}/>
        {unread > 0 && (
          <span data-testid="user-notif-badge"
                className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[#DC2626] text-white text-[9px] font-bold flex items-center justify-center">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>
      {open && (
        <div data-testid="user-notif-panel"
             className="absolute right-0 mt-2 w-96 bg-white border border-[#E5E2D9] rounded-md shadow-xl overflow-hidden z-50">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#E5E2D9] bg-[#FAFAF7]">
            <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] font-bold">
              Notifications
            </div>
            <div className="flex items-center gap-2">
              {unread > 0 && (
                <button onClick={markAll} data-testid="user-notif-mark-all"
                        className="text-[10px] text-[#2D5A27] font-bold hover:underline">
                  Mark all read
                </button>
              )}
              <button onClick={() => setOpen(false)}
                      className="p-1 text-[#5C5A55] hover:text-[#1A1A1A]">
                <X size={12}/>
              </button>
            </div>
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            {items.length === 0 && (
              <div className="p-6 text-center text-xs text-[#8A8780]">
                <ClipboardText size={22} weight="duotone" className="mx-auto mb-1 text-[#8A8780]"/>
                You're all caught up.
              </div>
            )}
            {items.map((n) => {
              const isUnread = !n.read_at;
              const href = n.service_request_id ? `/operations/service-requests/${n.service_request_id}` : null;
              const Row = href ? Link : "div";
              return (
                <Row key={n.id} to={href || undefined}
                     data-testid={`user-notif-item-${n.id}`}
                     onClick={() => { if (isUnread) markRead(n.id); }}
                     className={`block px-4 py-3 border-b border-[#F0ECE3] hover:bg-[#F7F6F2] transition-colors ${
                       isUnread ? "bg-[#FEF7E6]" : "bg-white"
                     }`}>
                  <div className="flex items-start gap-2">
                    {isUnread && (
                      <div className="w-2 h-2 rounded-full bg-[#DC2626] mt-1.5 shrink-0"/>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold text-[#1A1A1A] truncate">
                        {n.subject || "Notification"}
                      </div>
                      <div className="text-[11px] text-[#5C5A55] line-clamp-2 mt-0.5">
                        {n.body}
                      </div>
                      <div className="text-[10px] text-[#8A8780] mt-1">
                        {n.created_at ? new Date(n.created_at).toLocaleString("en-IN", {
                          day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
                        }) : ""}
                      </div>
                    </div>
                    {!isUnread && (
                      <CheckCircle size={12} weight="fill" className="text-[#15803D] mt-1 shrink-0"/>
                    )}
                  </div>
                </Row>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
